<template>
	<div class="mb-3">
		<label v-if="label">
			{{ label }} <span v-if="required" class="text-danger">*</span>
		</label>
		<div v-if="editor && showToolbar" class="menubar">
			<button
				type="button"
				:aria-label="$t('email-templates.format-heading')"
				:disabled="!editable"
				@mousedown="
					handleButtonMouseDown($event, 'toggleHeading', { level: 2 })
				"
				@click="
					$event.detail === 0 &&
						handleButtonMouseDown($event, 'toggleHeading', {
							level: 2
						})
				"
				:class="{
					'is-active': editor.isActive('heading', { level: 2 })
				}"
			>
				<Icon :icon="['far', 'heading']" class="text-black" />
			</button>
			<button
				type="button"
				:aria-label="$t('email-templates.bold')"
				@mousedown="handleButtonMouseDown($event, 'toggleBold')"
				@click="
					$event.detail === 0 &&
						handleButtonMouseDown($event, 'toggleBold')
				"
				:disabled="
					!editable ||
						!editor
							.can()
							.chain()
							.focus()
							.toggleBold()
							.run()
				"
				:class="{ 'is-active': editor.isActive('bold') }"
			>
				<Icon :icon="['far', 'bold']" class="text-black" />
			</button>
			<button
				type="button"
				:aria-label="$t('email-templates.italic')"
				@mousedown="handleButtonMouseDown($event, 'toggleItalic')"
				@click="
					$event.detail === 0 &&
						handleButtonMouseDown($event, 'toggleItalic')
				"
				:disabled="
					!editable ||
						!editor
							.can()
							.chain()
							.focus()
							.toggleItalic()
							.run()
				"
				:class="{ 'is-active': editor.isActive('italic') }"
			>
				<Icon :icon="['far', 'italic']" class="text-black" />
			</button>
			<button
				type="button"
				:aria-label="$t('email-templates.strike')"
				@mousedown="handleButtonMouseDown($event, 'toggleStrike')"
				@click="
					$event.detail === 0 &&
						handleButtonMouseDown($event, 'toggleStrike')
				"
				:disabled="
					!editable ||
						!editor
							.can()
							.chain()
							.focus()
							.toggleStrike()
							.run()
				"
				:class="{ 'is-active': editor.isActive('strike') }"
			>
				<Icon :icon="['far', 'strikethrough']" class="text-black" />
			</button>
			<button
				type="button"
				:aria-label="$t('email-templates.bullet-list')"
				:disabled="!editable"
				@mousedown="handleButtonMouseDown($event, 'toggleBulletList')"
				@click="
					$event.detail === 0 &&
						handleButtonMouseDown($event, 'toggleBulletList')
				"
				:class="{ 'is-active': editor.isActive('bulletList') }"
			>
				<Icon :icon="['far', 'list-ul']" class="text-black" />
			</button>
			<button
				type="button"
				:aria-label="$t('email-templates.ordered-list')"
				:disabled="!editable"
				@mousedown="handleButtonMouseDown($event, 'toggleOrderedList')"
				@click="
					$event.detail === 0 &&
						handleButtonMouseDown($event, 'toggleOrderedList')
				"
				:class="{ 'is-active': editor.isActive('orderedList') }"
			>
				<Icon :icon="['far', 'list-ol']" class="text-black" />
			</button>
			<button
				type="button"
				:aria-label="$t('email-templates.undo')"
				@mousedown="handleButtonMouseDown($event, 'undo')"
				@click="
					$event.detail === 0 && handleButtonMouseDown($event, 'undo')
				"
				:disabled="
					!editable ||
						!editor
							.can()
							.chain()
							.focus()
							.undo()
							.run()
				"
			>
				<Icon :icon="['far', 'undo']" class="text-black" />
			</button>
			<button
				type="button"
				:aria-label="$t('email-templates.redo')"
				@mousedown="handleButtonMouseDown($event, 'redo')"
				@click="
					$event.detail === 0 && handleButtonMouseDown($event, 'redo')
				"
				:disabled="
					!editable ||
						!editor
							.can()
							.chain()
							.focus()
							.redo()
							.run()
				"
			>
				<Icon :icon="['far', 'redo']" class="text-black" />
			</button>
			<slot name="toolbar-end" :editor="editor" />
		</div>
		<slot name="toolbar-after" :editor="editor" />
		<editor-content
			:editor="editor"
			class="tiptap-editor-content"
			:class="{ 'is-editor-empty': isEditorEmpty }"
			:data-placeholder="placeholder"
			:style="editorStyles"
		/>
	</div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-2'
import StarterKit from '@tiptap/starter-kit'
import Icon from '~/components/ui/common/Icon'

const HTML_LIST_TAG = /<(ul|ol|li)\b/i
const ORDERED_LIST_LINE = /^\s*\d+[.)]\s+(.+)$/
const UNORDERED_LIST_LINE = /^\s*(?:[-*•‣▪▫‒–—])\s+(.+)$/

export default {
	components: {
		EditorContent,
		Icon
	},
	props: {
		editable: { type: Boolean, default: true },
		extensions: { type: Array, default: () => [] },
		restricted: { type: Boolean, default: false },
		editorLabel: { type: String, default: '' },
		initialContent: {
			type: String,
			default: ''
		},
		label: {
			type: String,
			default: null
		},
		required: {
			type: Boolean,
			default: false
		},
		placeholder: {
			type: String,
			default: null
		},
		minHeight: {
			type: [Number, String],
			default: '5.5rem'
		},
		showToolbar: {
			type: Boolean,
			default: true
		}
	},
	data() {
		return {
			editor: null,
			isEditorEmpty: !this.initialContent
		}
	},
	computed: {
		editorStyles() {
			const minHeight =
				typeof this.minHeight === 'number'
					? `${this.minHeight}px`
					: this.minHeight

			return {
				'--tiptap-editor-min-height': minHeight
			}
		}
	},
	mounted() {
		this.editor = new Editor({
			editable: this.editable,
			content: this.initialContent,
			extensions: [
				this.restricted
					? StarterKit.configure({
							heading: { levels: [1, 2, 3] },
							code: false,
							codeBlock: false,
							blockquote: false,
							horizontalRule: false
					  })
					: StarterKit,
				...this.extensions
			],
			editorProps: {
				attributes: {
					'data-placeholder': this.placeholder || '',
					'aria-label':
						this.editorLabel || this.label || this.placeholder || ''
				},
				handlePaste: (view, event) => {
					void view
					return this.handlePaste(event)
				}
			},
			onUpdate: ({ editor }) => {
				this.isEditorEmpty = editor.isEmpty
				this.$emit('editorContentUpdated', editor.getHTML())
			}
		})
		this.isEditorEmpty = this.editor.isEmpty
		if (this.restricted)
			this.editor.commands.setContent(this.initialContent || '', false)
	},
	watch: {
		editable(value) {
			if (this.editor) this.editor.setEditable(value, false)
		},
		initialContent(newContent) {
			if (!this.editor || newContent === this.editor.getHTML()) {
				return
			}

			this.editor.commands.setContent(newContent || '', false)
			this.isEditorEmpty = this.editor.isEmpty
		}
	},
	methods: {
		handleButtonMouseDown(event, command, options = {}) {
			event.preventDefault()
			if (!this.editable) return
			this.editor
				.chain()
				.focus()
				[command](options)
				.run()
		},
		handlePaste(event) {
			const clipboardData =
				event.clipboardData ||
				(process.client ? window.clipboardData : null)

			if (!clipboardData) {
				return false
			}

			const html = clipboardData.getData('text/html') || ''
			if (html && (this.restricted || HTML_LIST_TAG.test(html))) {
				return false
			}

			const text =
				clipboardData.getData('text/plain') ||
				clipboardData.getData('text') ||
				''
			const convertedHtml = this.convertPlainTextListsToHtml(text)

			if (!convertedHtml || !this.editor?.commands?.insertContent) {
				return false
			}

			event.preventDefault()
			this.editor.commands.insertContent(convertedHtml)
			return true
		},
		convertPlainTextListsToHtml(text) {
			if (!text || !this.hasPlainTextList(text)) {
				return ''
			}

			const blocks = []
			let currentListType = null
			let currentListItems = []

			const flushList = () => {
				if (!currentListType || currentListItems.length === 0) {
					return
				}

				blocks.push(
					`<${currentListType}>${currentListItems
						.map(item => `<li>${item}</li>`)
						.join('')}</${currentListType}>`
				)
				currentListType = null
				currentListItems = []
			}

			text.replace(/\r\n?/g, '\n')
				.split('\n')
				.forEach(line => {
					const orderedListMatch = line.match(ORDERED_LIST_LINE)
					const unorderedListMatch = line.match(UNORDERED_LIST_LINE)
					const listType = orderedListMatch
						? 'ol'
						: unorderedListMatch
						? 'ul'
						: null
					const listContent =
						orderedListMatch?.[1] || unorderedListMatch?.[1]

					if (listType && listContent) {
						if (currentListType !== listType) {
							flushList()
							currentListType = listType
						}

						currentListItems.push(
							this.escapeHtml(listContent.trim())
						)
						return
					}

					flushList()

					const paragraph = line.trim()
					if (paragraph) {
						blocks.push(`<p>${this.escapeHtml(paragraph)}</p>`)
					}
				})

			flushList()

			return blocks.join('')
		},
		hasPlainTextList(text) {
			return text
				.replace(/\r\n?/g, '\n')
				.split('\n')
				.some(
					line =>
						ORDERED_LIST_LINE.test(line) ||
						UNORDERED_LIST_LINE.test(line)
				)
		},
		escapeHtml(value) {
			return String(value)
				.replace(/&/g, '&amp;')
				.replace(/</g, '&lt;')
				.replace(/>/g, '&gt;')
				.replace(/"/g, '&quot;')
				.replace(/'/g, '&#039;')
		}
	},
	beforeDestroy() {
		this.editor.destroy()
	}
}
</script>

<style>
.menubar {
	display: flex;
	margin-bottom: 0.5rem;
	gap: 0.3rem;
}

.menubar button {
	border: solid 1px #eaeaea;
	background: #ffffff;
	fill: #888888;
	cursor: pointer;
	height: 2rem;
	width: 2rem;
	border-radius: 0.3rem;
	display: flex;
	align-items: center;
	justify-content: center;
}

.menubar .is-active {
	background: #eaeaea;
	fill: #000000;
}

.ProseMirror {
	border: solid 1px #e3e3e3;
	padding: 1rem 1rem 0.5rem;
	border-radius: 0.3rem;
	color: #171717;
	min-height: var(--tiptap-editor-min-height, 5.5rem);
	position: relative;
	transition: box-shadow 0.1s;
}

.ProseMirror-focused {
	border: solid 1px #171717;
	padding: 1rem 1rem 0.5rem;
	border-radius: 0.3rem;
	color: #171717;
	min-height: var(--tiptap-editor-min-height, 5.5rem);
	outline: none;
	transition: box-shadow 0.1s;
	box-shadow: 0 0 0 3px #d7d7d7;
}

.tiptap-editor-content.is-editor-empty .ProseMirror::before {
	color: #a3a3a3;
	content: attr(data-placeholder);
	left: 1rem;
	pointer-events: none;
	position: absolute;
	top: 1rem;
}

.ProseMirror h2 {
	margin-bottom: 0.6rem;
	margin-top: 0;
	font-size: 1.5rem;
	@apply font-extrabold;
}

.ProseMirror h2 strong {
	font-weight: inherit;
}

.ProseMirror p {
	margin-bottom: 0.6rem;
}

.ProseMirror ul {
	list-style-type: disc;
	padding-left: 1.5rem;
	margin-bottom: 0.6rem;
}

.ProseMirror ol {
	list-style-type: decimal;
	padding-left: 1.5rem;
	margin-bottom: 0.6rem;
}

.ProseMirror ul p,
.ProseMirror ol p {
	margin-bottom: 0.3rem;
}

.ProseMirror li {
	margin-bottom: 0.2rem;
}

.ProseMirror strong {
	font-weight: 700;
}

.ProseMirror em {
	font-style: italic;
}

.ProseMirror s {
	text-decoration: line-through;
}

.menubar button:disabled {
	opacity: 0.6;
}
</style>
